import { createContext, useContext, useMemo, useReducer, type Dispatch, type ReactNode } from "react";
import { getDefaultTuningId, getDefaultVariantId, instruments } from "../data/instruments";
import type { UiMode } from "../audio/types";

export interface TimbroamState {
  selectedInstrumentId: string;
  selectedVariantId: string;
  selectedTuningId: string;
  audioReady: boolean;
  activeNotes: string[];
  uiMode: UiMode;
  culturalWarningVisible: boolean;
}

type TimbroamAction =
  | { type: "selectInstrument"; instrumentId: string }
  | { type: "selectVariant"; variantId: string }
  | { type: "selectTuning"; tuningId: string }
  | { type: "setAudioReady"; audioReady: boolean }
  | { type: "startNote"; noteName: string }
  | { type: "stopNote"; noteName: string }
  | { type: "setUiMode"; uiMode: UiMode }
  | { type: "setCulturalWarningVisible"; visible: boolean };

const initialInstrument = instruments[0];

export const initialTimbroamState: TimbroamState = {
  selectedInstrumentId: initialInstrument.id,
  selectedVariantId: initialInstrument.variants[0]?.id ?? "",
  selectedTuningId: initialInstrument.tunings[0]?.id ?? "",
  audioReady: false,
  activeNotes: [],
  uiMode: "performance",
  culturalWarningVisible: true
};

function timbroamReducer(state: TimbroamState, action: TimbroamAction): TimbroamState {
  switch (action.type) {
    case "selectInstrument":
      return {
        ...state,
        selectedInstrumentId: action.instrumentId,
        selectedVariantId: getDefaultVariantId(action.instrumentId),
        selectedTuningId: getDefaultTuningId(action.instrumentId),
        activeNotes: [],
        culturalWarningVisible: true
      };
    case "selectVariant":
      return { ...state, selectedVariantId: action.variantId };
    case "selectTuning":
      return { ...state, selectedTuningId: action.tuningId };
    case "setAudioReady":
      return { ...state, audioReady: action.audioReady };
    case "startNote":
      return state.activeNotes.includes(action.noteName)
        ? state
        : { ...state, activeNotes: [...state.activeNotes, action.noteName] };
    case "stopNote":
      return {
        ...state,
        activeNotes: state.activeNotes.filter((noteName) => noteName !== action.noteName)
      };
    case "setUiMode":
      return { ...state, uiMode: action.uiMode };
    case "setCulturalWarningVisible":
      return { ...state, culturalWarningVisible: action.visible };
    default:
      return state;
  }
}

interface TimbroamStateContextValue {
  state: TimbroamState;
  dispatch: Dispatch<TimbroamAction>;
}

const TimbroamStateContext = createContext<TimbroamStateContextValue | null>(null);

export function TimbroamStateProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(timbroamReducer, initialTimbroamState);
  const value = useMemo(() => ({ state, dispatch }), [state]);

  return <TimbroamStateContext.Provider value={value}>{children}</TimbroamStateContext.Provider>;
}

export function useTimbroamState(): TimbroamStateContextValue {
  const context = useContext(TimbroamStateContext);

  if (!context) {
    throw new Error("useTimbroamState must be used inside TimbroamStateProvider.");
  }

  return context;
}
